// @flow
import React, { useState } from 'react'

import Coverart from '@components/app/coverart'
import Share from '@components/app/share'
import Waveform from '@components/app/waveform'
import BuyModal from '@components/modals/buy'
import UIHiresBadge from '@components/ui/hires'
import UISample from '@components/ui/sample'
import PlayBtn from '@pages/media/sound/play'
import { formatDate } from '@utils/date'
import Link from 'next/link'
import { FormattedMessage } from 'react-intl'
import { SoundProps } from '../../../../types/sound'
import Userarea from './userarea'
import styles from './media.module.scss'

interface Props {
  sound: SoundProps
}

const SoundMedia = ({ sound }: Props) => {
  const [ isBuyOpen, setBuyOpen ] = useState(false)
  const [ isShareOpen, setShareOpen ] = useState(false)

  return (
    <div className={`d-flex mb-5 ${styles.media}`}>
      <div className={styles.coverartContainer}>
        <div className="ps-relative">
          <Link
            href={`/sound/${sound.id}/${sound.slug}`}
            aria-label={`More detail about ${sound.title}`}
          >
            <figure className={styles.coverartFigure}>
              <Coverart
                src={sound.coverart_crop_url}
                alt={sound.title}
                width={120}
              />
            </figure>
          </Link>
          <PlayBtn size="2rem" className={styles.playOnCover} sound={sound} />
        </div>
      </div>
      <div className={styles.detailContainer}>
        <h3 className="tx-22 mb-2">
          <Link
            href={`/sound/${sound.id}/${sound.slug}`}
            className="alt"
          >
            {sound.title}
          </Link>
        </h3>
        <p className="tx-14">
          <Link
            href={`/sound/${sound.id}/${sound.slug}`}
            className="nohover tx-gish-light"
          >
            {sound.description ? (
              <>
                {sound.description.substring(0, 100)}
                {sound.description.length > 100 && '...'}
              </>
            ) : (
              <>
                Released&nbsp;{formatDate(sound.released_at)}
              </>
            )}
          </Link>
        </p>
        <div className="tx-12 mb-3">
          <Userarea user={sound.user} />
          <span className="dot" />
          <time dateTime={sound.released_at.toString()}>
            {formatDate(sound.released_at)}
          </time>
        </div>
        {(sound.hi_resolution || sound.limitOn) && (
          <div className="d-flex align-items-center mb-2">
            {sound.hi_resolution && <UIHiresBadge className="mr-2" />}
            {sound.limitOn && <UISample />}
          </div>
        )}
        <Waveform
          id={sound.id}
          duration={sound.metadata?.duration ?? 0}
          src={sound.waveform_url}
          static={sound.limitOn}
        />
        <div className={`${styles.actions} ${styles.relative}`}>
          {/* <button type="button" className="btn-chromeless tx-black tx-12 mr-3">
            <i className="solicon-heart mr-2" />
            Like
            </button>
            <button type="button" className="btn-chromeless tx-black tx-12 mr-3">
            <i className="solicon-comment-alt mr-2" />
            Comments
          </button> */}
          <button
            type="button"
            className="btn-chromeless tx-black tx-12 mr-3"
            onClick={
              isShareOpen ? () => setShareOpen(false) : () => setShareOpen(true)
            }
          >
            <i className="solicon-share mr-2" />
            {isShareOpen ? (
              <FormattedMessage
                id="app.stopshare"
                defaultMessage="Stop Sharing"
              />
            ) : (
              <FormattedMessage id="app.share" defaultMessage="Share" />
            )}
          </button>
          <div
            className={
              isShareOpen
                ? `${styles.share} ${styles.show}`
                : `${styles.share} ${styles.hide}`
            }
          >
            <Share
              type="sound"
              id={sound.id}
              url={`https://sdn.lv/s${sound.id}`}
              text={`${sound.title} by ${sound.user.name}`}
            />
          </div>
          {sound.onSale && (
            <button
              type="button"
              className="btn-chromeless tx-black tx-12 mr-3"
              onClick={() => setBuyOpen(true)}
            >
              <i className="solicon-tag-cord mr-2" />
              <FormattedMessage
                id="app.buy.sound"
                defaultMessage="Buy this sound"
              />
            </button>
          )}
        </div>
      </div>
      <BuyModal
        category="sound"
        data={sound}
        isOpen={isBuyOpen}
        onClose={() => setBuyOpen(false)}
      />
    </div>
  )
}

export default SoundMedia
